import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SnackbarService } from '../../../services/design/snackbar.service';
import {
  AdminPayoutRequest,
  PayoutListResponse,
  PayoutRequestStatus,
  PayoutRequestsService,
} from '../../../services/riders/payout-requests.service';
import { Rider, RiderService } from '../../../services/riders/rider.service';
import { StoreOption, StoreService } from '../../../services/stores/store.service';
import { IndianCompactPipe } from '../../../pipes/indian-compact.pipe';
import { AuthService } from '../../../services/auth/auth.service';

@Component({
  selector: 'app-rider-payouts',
  standalone: true,
  imports: [CommonModule, FormsModule, IndianCompactPipe],
  templateUrl: './rider-payouts.component.html',
})
export class RiderPayoutsComponent implements OnInit {
  readonly auth=inject(AuthService);
  private payoutService = inject(PayoutRequestsService);
  private riderService = inject(RiderService);
  private storeService = inject(StoreService);
  private snackbar = inject(SnackbarService);

  payouts = signal<AdminPayoutRequest[]>([]);
  totals = signal<PayoutListResponse['totals']>({
    requested: 0,
    pending: 0,
    approved: 0,
    paid: 0,
    rejected: 0,
  });
  stores = signal<StoreOption[]>([]);
  riders = signal<Rider[]>([]);

  activeFilter: PayoutRequestStatus = 'ALL';
  selectedStore = 0;
  selectedRiderId = '';
  fromDate = '';
  toDate = '';
  page = 1;
  pageSize = 10;
  totalItems = 0;
  Math = Math;

  actionPayout = signal<AdminPayoutRequest | null>(null);
  actionType = signal<'reject' | 'paid' | null>(null);
  actionRemarks = '';
  actionTransactionRef = '';
  isSubmitting = signal(false);

  readonly statusTabs: PayoutRequestStatus[] = [
    'ALL',
    'pending',
    'approved',
    'paid',
    'rejected',
  ];

  ngOnInit(): void {
    this.selectedStore=this.auth.isSuperAdmin()?0:Number(this.auth.storeId());
    this.storeService.allStores().subscribe({
      next: (data) => this.stores.set(data.filter(s=>this.auth.isSuperAdmin()||Number(s.id)===this.selectedStore)),
    });
    this.riderService.getFinanceRiders().subscribe({
      next: (data) => this.riders.set(data),
    });
    this.loadPayouts();
  }

  loadPayouts() {
    this.payoutService
      .list({
        status: this.activeFilter,
        riderId: this.selectedRiderId || undefined,
        fromDate: this.fromDate ? new Date(this.fromDate).toISOString() : undefined,
        toDate: this.toDate
          ? new Date(`${this.toDate}T23:59:59`).toISOString()
          : undefined,
        storeId: this.selectedStore > 0 ? this.selectedStore : undefined,
        page: this.page,
        pageSize: this.pageSize,
      })
      .subscribe({
        next: (res) => {
          this.payouts.set(res.data);
          this.totalItems = res.count;
          this.totals.set(res.totals);
        },
        error: (err) => {
          console.error(err);
          this.snackbar.error(err?.message ?? 'Failed to load payout requests');
        },
      });
  }

  setFilter(filter: PayoutRequestStatus) {
    this.activeFilter = filter;
    this.page = 1;
    this.loadPayouts();
  }

  onPageChange(newPage: number) {
    this.page = newPage;
    this.loadPayouts();
  }

  onPageSizeChange(event: Event) {
    this.pageSize = Number((event.target as HTMLSelectElement).value);
    this.page = 1;
    this.loadPayouts();
  }

  get totalPages() {
    return Math.ceil(this.totalItems / this.pageSize);
  }

  statusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'badge-warning';
      case 'approved':
        return 'badge-info';
      case 'paid':
        return 'badge-success';
      case 'rejected':
        return 'badge-error';
      default:
        return 'badge-ghost';
    }
  }

  approvePayout(payout: AdminPayoutRequest) {
    if (!confirm(`Approve payout request #${payout.id} for ₹${payout.amount}?`)) {
      return;
    }

    this.isSubmitting.set(true);
    this.payoutService.approve(payout.id).subscribe({
      next: () => {
        this.snackbar.success('Payout request approved');
        this.loadPayouts();
        this.isSubmitting.set(false);
      },
      error: (err) => {
        this.snackbar.error(err?.message ?? 'Failed to approve payout');
        this.isSubmitting.set(false);
      },
    });
  }

  openReject(payout: AdminPayoutRequest) {
    this.actionPayout.set(payout);
    this.actionType.set('reject');
    this.actionRemarks = '';
  }

  openMarkPaid(payout: AdminPayoutRequest) {
    this.actionPayout.set(payout);
    this.actionType.set('paid');
    this.actionRemarks = '';
    this.actionTransactionRef = '';
  }

  closeActionModal() {
    this.actionPayout.set(null);
    this.actionType.set(null);
    this.actionRemarks = '';
    this.actionTransactionRef = '';
  }

  submitAction() {
    const payout = this.actionPayout();
    const type = this.actionType();
    if (!payout || !type) return;

    this.isSubmitting.set(true);

    if (type === 'reject') {
      if (!this.actionRemarks.trim()) {
        this.snackbar.warning('Rejection reason is required');
        this.isSubmitting.set(false);
        return;
      }

      this.payoutService.reject(payout.id, this.actionRemarks.trim()).subscribe({
        next: () => {
          this.snackbar.success('Payout request rejected');
          this.closeActionModal();
          this.loadPayouts();
          this.isSubmitting.set(false);
        },
        error: (err) => {
          this.snackbar.error(err?.message ?? 'Failed to reject payout');
          this.isSubmitting.set(false);
        },
      });
      return;
    }

    if (!this.actionTransactionRef.trim()) {
      this.snackbar.warning('Transaction reference (UTR/UPI) is required');
      this.isSubmitting.set(false);
      return;
    }

    this.payoutService
      .markPaid(
        payout.id,
        this.actionTransactionRef.trim(),
        this.actionRemarks.trim() || undefined
      )
      .subscribe({
        next: () => {
          this.snackbar.success('Payout marked as paid and wallet debited');
          this.closeActionModal();
          this.loadPayouts();
          this.isSubmitting.set(false);
        },
        error: (err) => {
          this.snackbar.error(err?.message ?? 'Failed to mark payout as paid');
          this.isSubmitting.set(false);
        },
      });
  }
}
